import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    generateChecklistClientId,
    hydrateChecklistItems,
    normalizeChecklistSortOrder,
    prepareChecklistRequestItems,
} from '@features/Checklist/lib/helpers';
import { IChecklistTask, IChecklistTaskDto } from '@features/Checklist/model/types';

interface IUseChecklistDraftProps {
    checklistData?: IChecklistTaskDto[];
    isEditMode: boolean;
}

interface IAppendChecklistItemInput {
    text: string;
    isChecked?: boolean;
}

export const useChecklistDraft = ({ checklistData, isEditMode }: IUseChecklistDraftProps) => {
    const [persistedChecklist, setPersistedChecklist] = useState<IChecklistTask[]>([]);
    const [rawChecklist, setRawChecklist] = useState<IChecklistTask[]>([]);
    const [isChecklistHydrated, setIsChecklistHydrated] = useState(false);
    const [lastAddedChecklistItemClientId, setLastAddedChecklistItemClientId] = useState<
        string | null
    >(null);

    useEffect(() => {
        setIsChecklistHydrated(false);

        if (!checklistData) {
            setPersistedChecklist([]);

            if (!isEditMode) {
                setRawChecklist([]);
            }

            setIsChecklistHydrated(true);

            return;
        }

        setPersistedChecklist((prevPersistedChecklist) => {
            const nextPersistedChecklist = hydrateChecklistItems(
                checklistData,
                prevPersistedChecklist
            );

            if (!isEditMode) {
                setRawChecklist(nextPersistedChecklist);
            }

            return nextPersistedChecklist;
        });
        setIsChecklistHydrated(true);
    }, [checklistData, isEditMode]);

    const disableToUpdate = useMemo(
        () => rawChecklist.every((item) => item.text.trim().length === 0),
        [rawChecklist]
    );

    const resetDraftToPersisted = useCallback(() => {
        setRawChecklist(persistedChecklist);
        setLastAddedChecklistItemClientId(null);
    }, [persistedChecklist]);

    const resetChecklist = useCallback(() => {
        setPersistedChecklist([]);
        setRawChecklist([]);
        setLastAddedChecklistItemClientId(null);
    }, []);

    const restoreRawChecklist = useCallback((items: IChecklistTask[]) => {
        setRawChecklist(items);
    }, []);

    const applyPersistedChecklist = useCallback((items: IChecklistTaskDto[]) => {
        setPersistedChecklist((prevChecklist) => hydrateChecklistItems(items, prevChecklist));
        setRawChecklist((prevChecklist) => hydrateChecklistItems(items, prevChecklist));
    }, []);

    const appendChecklistItems = useCallback((items: IAppendChecklistItemInput[]) => {
        const nextItems = items
            .map(({ text, isChecked = false }) => ({
                clientId: generateChecklistClientId(),
                text: text.trim(),
                isChecked,
                sortOrder: 0,
            }))
            .filter((item) => item.text.length > 0);

        if (nextItems.length === 0) {
            return false;
        }

        setRawChecklist((prevChecklist) =>
            normalizeChecklistSortOrder([...prevChecklist, ...nextItems])
        );

        return true;
    }, []);

    const addChecklistItem = useCallback((clientId?: string) => {
        if (typeof clientId !== 'string') {
            const nextClientId = generateChecklistClientId();

            setLastAddedChecklistItemClientId(nextClientId);
            setRawChecklist((prevChecklist) => [
                ...prevChecklist,
                {
                    clientId: nextClientId,
                    sortOrder: prevChecklist.length + 1,
                    text: '',
                    isChecked: false,
                },
            ]);
            return;
        }

        setRawChecklist((prevChecklist) => {
            const foundIndex = prevChecklist.findIndex((item) => item.clientId === clientId);

            if (foundIndex === -1) {
                return prevChecklist;
            }

            const insertedSortOrder = prevChecklist[foundIndex].sortOrder + 1;
            const nextClientId = generateChecklistClientId();

            setLastAddedChecklistItemClientId(nextClientId);

            return prevChecklist.reduce<typeof prevChecklist>((acc, item, index) => {
                acc.push(item);

                if (index === foundIndex) {
                    acc.push({
                        clientId: nextClientId,
                        sortOrder: insertedSortOrder,
                        text: '',
                        isChecked: false,
                    });
                    return acc;
                }

                if (index > foundIndex) {
                    acc[acc.length - 1] = {
                        ...item,
                        sortOrder: item.sortOrder + 1,
                    };
                }

                return acc;
            }, []);
        });
    }, []);

    const changeChecklistItemText = useCallback((clientId: string, text: string) => {
        setRawChecklist((prevChecklist) =>
            prevChecklist.map((item) => (item.clientId === clientId ? { ...item, text } : item))
        );
    }, []);

    const toggleChecklistItem = useCallback(
        (clientId: string) => {
            const toggledChecklistItem = rawChecklist.find((item) => item.clientId === clientId);

            if (!toggledChecklistItem) {
                return undefined;
            }

            setRawChecklist((prevChecklist) =>
                prevChecklist.map((item) =>
                    item.clientId === clientId ? { ...item, isChecked: !item.isChecked } : item
                )
            );

            return toggledChecklistItem.id;
        },
        [rawChecklist]
    );

    const deleteChecklistItem = useCallback((clientId: string) => {
        setLastAddedChecklistItemClientId((prevClientId) =>
            prevClientId === clientId ? null : prevClientId
        );
        setRawChecklist((prevChecklist) =>
            normalizeChecklistSortOrder(prevChecklist.filter((item) => item.clientId !== clientId))
        );
    }, []);

    const moveChecklistItemToBottom = useCallback((clientId: string) => {
        setRawChecklist((prevChecklist) => {
            const orderedChecklist = [...prevChecklist].sort((a, b) => a.sortOrder - b.sortOrder);
            const movedChecklistItem = orderedChecklist.find((item) => item.clientId === clientId);

            if (!movedChecklistItem) {
                return prevChecklist;
            }

            const nextChecklist = orderedChecklist.filter((item) => item.clientId !== clientId);

            nextChecklist.push(movedChecklistItem);

            return normalizeChecklistSortOrder(nextChecklist);
        });
    }, []);

    const reorderChecklistItem = useCallback((activeClientId: string, overClientId: string) => {
        if (activeClientId === overClientId) {
            return;
        }

        setRawChecklist((prevChecklist) => {
            const orderedChecklist = [...prevChecklist].sort((a, b) => a.sortOrder - b.sortOrder);
            const activeIndex = orderedChecklist.findIndex(
                (item) => item.clientId === activeClientId
            );
            const overIndex = orderedChecklist.findIndex((item) => item.clientId === overClientId);

            if (activeIndex === -1 || overIndex === -1) {
                return prevChecklist;
            }

            const nextChecklist = [...orderedChecklist];
            const [movedItem] = nextChecklist.splice(activeIndex, 1);

            nextChecklist.splice(overIndex, 0, movedItem);

            return normalizeChecklistSortOrder(nextChecklist);
        });
    }, []);

    return {
        isChecklistHydrated,
        persistedChecklist,
        rawChecklist,
        disableToUpdate,
        lastAddedChecklistItemClientId,
        appendChecklistItems,
        resetDraftToPersisted,
        resetChecklist,
        restoreRawChecklist,
        applyPersistedChecklist,
        prepareUpdatePayload: () => prepareChecklistRequestItems(rawChecklist),
        addChecklistItem,
        changeChecklistItemText,
        toggleChecklistItem,
        deleteChecklistItem,
        moveChecklistItemToBottom,
        reorderChecklistItem,
    };
};
